import React, { useState } from "react";
import { useT } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageSquare } from "lucide-react";

const SUPPORT_EMAIL = "support@innercompass.tech";
const PRIVACY_EMAIL = "privacy@innercompass.tech";

export default function Contact() {
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const openMail = (e) => {
    e.preventDefault();
    const body =
      `${t("public.contact.mail.name")}: ${name || "-"}\n` +
      `${t("public.contact.mail.email")}: ${email || "-"}\n\n` +
      `${message || ""}`;
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject || t("public.contact.mail.subjectDefault"))}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16" data-testid="page-contact">
      <header className="mb-8">
        <h1 className="font-heading text-3xl md:text-5xl tracking-tight text-ink-900">{t("public.contact.title")}</h1>
        <p className="mt-3 text-ink-500 text-base md:text-lg max-w-2xl">{t("public.contact.subtitle")}</p>
      </header>

      <div className="grid gap-6 md:grid-cols-2 mb-10">
        <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-support-email" className="rounded-3xl bg-white shadow-card p-6 hover:-translate-y-0.5 transition">
          <div className="flex items-center gap-2 mb-2"><Mail className="h-5 w-5 text-sage-600" strokeWidth={1.75} /><div className="font-heading text-lg">{t("public.contact.support.title")}</div></div>
          <p className="text-sm text-ink-700 mb-2">{t("public.contact.support.body")}</p>
          <div className="text-sage-700 text-sm">{SUPPORT_EMAIL}</div>
        </a>
        <a href={`mailto:${PRIVACY_EMAIL}`} data-testid="contact-privacy-email" className="rounded-3xl bg-white shadow-card p-6 hover:-translate-y-0.5 transition">
          <div className="flex items-center gap-2 mb-2"><MessageSquare className="h-5 w-5 text-sage-600" strokeWidth={1.75} /><div className="font-heading text-lg">{t("public.contact.privacy.title")}</div></div>
          <p className="text-sm text-ink-700 mb-2">{t("public.contact.privacy.body")}</p>
          <div className="text-sage-700 text-sm">{PRIVACY_EMAIL}</div>
        </a>
      </div>

      <form onSubmit={openMail} className="rounded-3xl bg-white shadow-card p-6 md:p-8 space-y-4" data-testid="contact-form">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-5 w-5 text-sage-600" strokeWidth={1.75} />
          <h2 className="font-heading text-xl">{t("public.contact.form.heading")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>{t("common.name")}</Label>
            <Input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl bg-cream-50 h-11 mt-1" />
          </div>
          <div>
            <Label>{t("common.email")}</Label>
            <Input type="email" data-testid="contact-email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl bg-cream-50 h-11 mt-1" />
          </div>
        </div>
        <div>
          <Label>{t("public.contact.form.subject")}</Label>
          <Input data-testid="contact-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl bg-cream-50 h-11 mt-1" />
        </div>
        <div>
          <Label>{t("public.contact.form.message")}</Label>
          <Textarea data-testid="contact-message" value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-xl bg-cream-50 mt-1 min-h-32" required />
        </div>
        <div className="flex justify-end">
          <Button type="submit" data-testid="contact-submit" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-6">
            {t("public.contact.form.submit")}
          </Button>
        </div>
        <p className="text-xs text-ink-500">{t("public.contact.form.mailto_note")}</p>
      </form>
    </div>
  );
}
